import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { allTvSeries } from '@/mocks/tvSeries';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const articleId = Number(id);

  const tvSeries = allTvSeries.find((item) => item.id === articleId);
  const article = articles.find((item) => item.id === articleId);
  const item = tvSeries || article;

  if (!item) {
    return (
      <main className="min-h-screen bg-background-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-500 mb-4">页面未找到</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const isTv = 'actors' in item;
  const pageTitle = isTv
    ? `${item.title} - 成全影视 | 免费在线观看`
    : `${item.title} - 成全影视`;
  const pageDesc = isTv
    ? `${item.title}全集免费在线观看，成全影视提供${item.title}高清播放，不缓冲流畅追剧，免VIP看完整版。主演：${item.actors.join('、')}。`
    : item.summary;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: item.title,
    description: pageDesc,
    datePublished: 'publishDate' in item ? item.publishDate : `${item.year}-01-01`,
    author: {
      '@type': 'Organization',
      name: '成全影视',
    },
  };

  const tvSchema = isTv ? {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: item.title,
    description: item.description,
    dateCreated: `${item.year}`,
    director: { '@type': 'Person', name: item.director },
    actor: item.actors.map((a: string) => ({ '@type': 'Person', name: a })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: item.rating,
      bestRating: 10,
    },
  } : null;

  const relatedItems = allTvSeries
    .filter((s) => isTv && s.category === item.category && s.id !== item.id)
    .slice(0, 6);

  const moreArticles = !isTv
    ? articles.filter((a) => a.id !== item.id).slice(0, 4)
    : [];

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={`${item.title},成全影视,${'categoryName' in item ? item.categoryName : ''},在线观看,免费`} />
      <link rel="canonical" href={`https://alu-expo-1.cn/article/${id}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://alu-expo-1.cn/article/${id}`} />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      {tvSchema && <script type="application/ld+json">{JSON.stringify(tvSchema)}</script>}

      <main className="min-h-screen bg-background-50 pt-16">
        <article className="max-w-[1040px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: isTv ? item.category : ('categoryName' in item ? item.categoryName : ''), href: isTv ? `/category/${item.category}` : `/category/${('category' in item ? item.category : 'dianshiju')}` },
              { label: item.title },
            ]}
          />

          {isTv ? (
            <div className="mt-8">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="w-full md:w-[300px] shrink-0">
                  <div className="rounded-xl overflow-hidden aspect-[2/3]">
                    <img
                      src={item.coverImage}
                      alt={item.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    {item.title}
                  </h1>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-2 py-0.5 bg-primary-500 text-white text-sm font-bold rounded">
                      {item.rating}
                    </span>
                    <span className="text-sm text-foreground-500">/ 10</span>
                  </div>
                  <div className="grid grid-cols-2 gap-y-2 gap-x-6 text-sm mb-6">
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-400">{t('director')}</span>
                      <span className="text-foreground-700 font-medium">{item.director}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-400">{t('year')}</span>
                      <span className="text-foreground-700 font-medium">{item.year}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-400">{t('region')}</span>
                      <span className="text-foreground-700 font-medium">{item.region}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-400">{t('updateInfo')}</span>
                      <span className="text-foreground-700 font-medium text-primary-500">{item.updateInfo}</span>
                    </div>
                    <div className="col-span-2 flex items-start gap-2">
                      <span className="text-foreground-400 shrink-0">{t('actors')}</span>
                      <span className="text-foreground-700 font-medium">{item.actors.join(' / ')}</span>
                    </div>
                  </div>
                  <div className="bg-background-100 rounded-xl p-5">
                    <h2 className="text-base font-semibold text-foreground-900 mb-3">剧情简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>

              <hr className="border-background-200 my-10" />

              <section>
                <h2 className="text-lg font-bold text-foreground-900 mb-5">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {relatedItems.map((r) => (
                    <Link
                      key={r.id}
                      to={`/article/${r.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-2">
                        <img
                          src={r.coverImage}
                          alt={r.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                          loading="lazy"
                        />
                        <div className="absolute top-1.5 left-1.5">
                          <span className="px-1 py-0.5 bg-primary-500 text-white text-[10px] font-bold rounded">
                            {r.rating}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-xs font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {r.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          ) : (
            <div className="mt-8">
              <div className="mb-8">
                <div className="rounded-xl overflow-hidden h-[300px] md:h-[400px] mb-6">
                  <img
                    src={item.coverImage}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">{item.title}</h1>
                <div className="flex items-center gap-4 text-xs text-foreground-400 mb-6">
                  <span>{'publishDate' in item ? item.publishDate : ''}</span>
                  <span>{'author' in item ? item.author : ''}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {'readCount' in item ? `${(item.readCount / 10000).toFixed(1)}万${t('readCount')}` : ''}
                  </span>
                </div>
                <div
                  className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: 'content' in item ? item.content : '' }}
                />
                {'tags' in item && (
                  <div className="flex flex-wrap gap-2 mt-8">
                    {item.tags.map((tag: string) => (
                      <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-500 text-xs rounded-full">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {moreArticles.length > 0 && (
                <>
                  <hr className="border-background-200 my-10" />
                  <section>
                    <h2 className="text-lg font-bold text-foreground-900 mb-5">{t('relatedArticles')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {moreArticles.map((a) => (
                        <Link
                          key={a.id}
                          to={`/article/${a.id}`}
                          className="group cursor-pointer flex gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200/60 transition-colors"
                        >
                          <div className="w-24 h-16 shrink-0 rounded-lg overflow-hidden">
                            <img
                              src={a.coverImage}
                              alt={a.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
                              {a.title}
                            </h3>
                            <span className="text-[11px] text-foreground-400">{a.publishDate}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                </>
              )}
            </div>
          )}
        </article>
      </main>
    </>
  );
}